// biome-ignore-all lint/suspicious/noArrayIndexKey: index keys are safe here; lists are derived from render state in a stable order and items are not reordered or removed mid-render (Task #32).
// =============================================================================
// ChildLaptopSection.jsx
// =============================================================================
// Per-child device form section used inside the request wizard.
//
// Renders: device-type picker, estimated cost (auto-fills from standardCosts
//          when type chosen), delivery address (DeliverySelect), BYOD URL,
//          school-device declaration, internet-safety + terms checkboxes,
//          and "why needed" rationale. TAFE-specific fields appear when the
//          child's yearLevel is TAFE.
//
// Props:
//   laptop             - { deviceType, estimatedCost, byod, deliveryAddress,
//                          hasSchoolDevice, internetSafety, termsAccepted,
//                          whyNeeded, tafeCourse, tafeStartDate, tafeLength, ... }
//   onChange           - called with the merged updated object
//   yearLevel          - "TAFE" toggles TAFE-specific fields
//   user               - current user; staff get an estimated cost field
//   standardCosts      - catalogue used by deviceCost() for the auto-fill
//   deliveryAddresses  - registered addresses for the shelter (for DeliverySelect)
//   generalDelivery    - request-level delivery address; used as the fallback if
//                        laptop.deliveryAddress is null/undefined
//   hasError           - if true, highlight section border/header as invalid
//
// Notes: mount effect prefills laptop.deliveryAddress + laptop.estimatedCost
//        from generalDelivery / deviceCost when those are empty/null. Re-fires
//        when deviceType or costs change so the saved cost matches the UI.
//
// SEE ALSO
//   src/components/form/ChildSections.jsx — re-exports this component
//   src/utils/formConstants.js            — DEVICE_TYPES / deviceCost / DEFAULT_STANDARD_COSTS
// =============================================================================
import React from "react";
import { isResponderJob } from "../../app/access";
import { useShowDeviceDelivery } from "../../app/UiConfigContext.jsx";
import { uploadFile } from "../../data/storage";
import { alertDialog } from "../../services/dialogService";
import { validateUploadFile } from "../../services/fileTypeGuard";
import { PANEL } from "../../utils/constants";
import { DEFAULT_STANDARD_COSTS, DEVICE_TYPES, deviceCost } from "../../utils/formConstants";
import { logger } from "../../utils/logger";
import { sanitizeMoney } from "../../utils/sanitize";
import { DeliverySelect } from "./DeliverySelect.jsx";
import { FileField } from "./FormUI.jsx";
import { HelpButton } from "./HelpButton.jsx";

function ChildLaptopSection({
  laptop,
  onChange,
  yearLevel,
  user,
  standardCosts,
  deliveryAddresses,
  generalDelivery,
  hasError,
}) {
  var deviceTypeId = React.useId();
  var costId = React.useId();
  var whyNeededId = React.useId();
  var courseNameId = React.useId();
  var courseStartId = React.useId();
  var courseLengthId = React.useId();
  var costs = standardCosts || DEFAULT_STANDARD_COSTS;
  var isTafe = yearLevel === "TAFE";
  // Admin-controlled: whether the per-device delivery address shows (default off —
  // each request now carries a request-level delivery address). System Setup → Config.
  const showDeviceDelivery = useShowDeviceDelivery();

  function up(fields) {
    onChange(Object.assign({}, laptop, fields));
  }

  var deviceType = laptop?.deviceType || "";
  var estimatedCost =
    laptop?.estimatedCost || (deviceType ? String(deviceCost(costs, deviceType)) : "");
  var whyNeeded = laptop?.whyNeeded || "";
  var deliveryAddress =
    laptop && laptop.deliveryAddress !== undefined && laptop.deliveryAddress !== null
      ? laptop.deliveryAddress
      : generalDelivery || "";

  // Prefill deliveryAddress and estimatedCost when empty. Re-fires when the
  // user picks a deviceType (so DB save captures the preset cost, not 0) or
  // when the request-level delivery changes (so an unchanged laptop adopts it).
  // The `!laptop?.field` guards prevent overwriting user input.
  React.useEffect(
    function () {
      var updates = {};
      if (generalDelivery && laptop?.deliveryAddress == null) {
        updates.deliveryAddress = generalDelivery;
      }
      if (deviceType && !laptop?.estimatedCost) {
        var preset = deviceCost(costs, deviceType);
        if (preset) updates.estimatedCost = String(preset);
      }
      if (Object.keys(updates).length > 0) up(updates);
      // `up` and `laptop` intentionally excluded — `up` is recreated each render
      // and would loop; we only react to the inputs that actually drive the
      // prefill decision.
    },
    [deviceType, costs, generalDelivery, laptop?.estimatedCost, laptop?.deliveryAddress]
  );
  var hasSchoolDevice = !!laptop?.hasSchoolDevice;
  var internetSafety = !!laptop?.internetSafety;
  var termsAccepted = !!laptop?.termsAccepted;
  var byod = laptop?.byod || null;

  return (
    <div
      style={{
        border: `1px solid ${hasError ? "#C62828" : "#CFE0DE"}`,
        borderRadius: 10,
        marginBottom: 14,
        overflow: "hidden",
      }}
    >
      <div
        style={{
          background: hasError ? "#FFEBEE" : PANEL.bg,
          borderBottom: `1px solid ${hasError ? "#EF9A9A" : PANEL.border}`,
          padding: "10px 16px",
        }}
      >
        <span
          style={{ fontWeight: 700, fontSize: 14, color: hasError ? "#C62828" : PANEL.heading }}
        >
          Device
        </span>
      </div>
      <div style={{ padding: "12px 16px" }}>
        <div
          style={{
            background: "#FFF3E0",
            border: "1px solid #FFB300",
            borderRadius: 8,
            padding: "14px 18px",
            marginBottom: 16,
            fontSize: 13,
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: "#E65100" }}>
            {"TERMS AND CONDITIONS: "}
            <HelpButton helpKey="laptop" user={user} />
            <span style={{ marginLeft: 12, fontWeight: 400, fontSize: 13 }}>
              Eligibility criteria
              <HelpButton helpKey="laptop_eligibility" user={user} />
            </span>
          </div>
          <div style={{ color: "#1565C0", marginBottom: 10 }}>
            By submitting this request you confirm that the parent/guardian of the child has
            accepted these terms and conditions.
          </div>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>
            THE SUPPLY OF A LAPTOP OR iPAD IS SUBJECT TO THE FOLLOWING CONDITIONS:
          </div>
          {/* CSS multi-column (not a grid): columns flow independently, so a
              taller item in one column can't leave an empty gap opposite a
              shorter item. breakInside avoids splitting an item across columns. */}
          <div style={{ columnCount: 2, columnGap: 24 }}>
            {[
              "You confirm that the student is enrolled at the nominated school.",
              "Zephyr will not be responsible for the repairs or upkeep of the laptop.",
              "Zephyr will not be responsible for any extras, including internet access or usage fees.",
              "Zephyr Education will not be responsible for any use or misuse of the laptop.",
              "Please note laptops have Windows 11 installed.",
              "Student versions of Microsoft Office are NOT included with the device.",
              "They need to be supplied and downloaded through the individual schools.",
            ].map(function (item, i) {
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    gap: 6,
                    alignItems: "flex-start",
                    padding: "3px 0",
                    breakInside: "avoid",
                  }}
                >
                  <span style={{ color: "#E65100", flexShrink: 0 }}>•</span>
                  <span style={{ fontWeight: i >= 4 ? 700 : 400 }}>{item}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: isResponderJob(user) ? "1fr 1fr" : "1fr",
            gap: 14,
            marginBottom: 14,
          }}
        >
          <div>
            <label
              htmlFor={deviceTypeId}
              style={{
                display: "block",
                fontSize: 13,
                fontWeight: 600,
                color: "#1A2E2B",
                marginBottom: 5,
              }}
            >
              Device Type <span style={{ color: "#C62828" }}>*</span>
              {!isResponderJob(user) && (
                <HelpButton helpKey="device_specs" label="Device Specs" user={user} />
              )}
            </label>
            <select
              id={deviceTypeId}
              value={deviceType}
              onChange={function (e) {
                var v = e.target.value;
                var preset = deviceCost(costs, v);
                up({ deviceType: v, estimatedCost: preset ? String(preset) : estimatedCost });
              }}
              style={{
                width: "100%",
                padding: "9px 12px",
                borderRadius: 7,
                border: `1px solid ${"#CFE0DE"}`,
                fontSize: 14,
                fontFamily: "inherit",
                background: "#FFFFFF",
                boxSizing: "border-box",
              }}
            >
              <option value="">Select...</option>
              {DEVICE_TYPES.map(function (d) {
                return React.createElement("option", { key: d, value: d }, d);
              })}
            </select>
          </div>
          {isResponderJob(user) && (
            <div>
              <label
                htmlFor={costId}
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#1A2E2B",
                  marginBottom: 5,
                }}
              >
                Estimated Cost ($){" "}
                <span style={{ fontWeight: 400, color: "#607D79", fontSize: 12 }}>
                  pre-filled, adjustable
                </span>
              </label>
              <input
                id={costId}
                type="text"
                inputMode="decimal"
                value={estimatedCost}
                onChange={function (e) {
                  up({ estimatedCost: sanitizeMoney(e.target.value) });
                }}
                placeholder="0"
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: 7,
                  border: `1px solid ${"#CFE0DE"}`,
                  fontSize: 14,
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
              />
              <div style={{ fontSize: 11, color: "#607D79", marginTop: 3 }}>
                Shown to DFV Service as an estimate only
              </div>
            </div>
          )}
        </div>

        <div style={{ marginBottom: 14 }}>
          <label
            htmlFor={whyNeededId}
            style={{
              display: "block",
              fontSize: 13,
              fontWeight: 600,
              color: "#1A2E2B",
              marginBottom: 5,
            }}
          >
            Why is a Device needed and what will it mainly be used for?{" "}
            <span style={{ color: "#C62828" }}>*</span>
          </label>
          <textarea
            id={whyNeededId}
            value={whyNeeded}
            onChange={function (e) {
              up({ whyNeeded: e.target.value });
            }}
            rows={3}
            placeholder="Please explain..."
            style={{
              width: "100%",
              padding: "9px 12px",
              borderRadius: 7,
              border: `1px solid ${"#CFE0DE"}`,
              fontSize: 14,
              fontFamily: "inherit",
              boxSizing: "border-box",
              resize: "vertical",
            }}
          />
        </div>

        {isTafe && (
          <div
            style={{
              marginBottom: 14,
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 14,
            }}
          >
            <div>
              <label
                htmlFor={courseNameId}
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#1A2E2B",
                  marginBottom: 5,
                }}
              >
                Course Name <span style={{ color: "#C62828" }}>*</span>
              </label>
              <input
                id={courseNameId}
                type="text"
                value={laptop?.tafeCourse || ""}
                onChange={function (e) {
                  up({ tafeCourse: e.target.value });
                }}
                placeholder="e.g. Certificate III in Business"
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: 7,
                  border: `1px solid ${"#CFE0DE"}`,
                  fontSize: 14,
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <label
                htmlFor={courseStartId}
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#1A2E2B",
                  marginBottom: 5,
                }}
              >
                Course Start Date <span style={{ color: "#C62828" }}>*</span>
              </label>
              <input
                id={courseStartId}
                type="date"
                value={laptop?.tafeStartDate || ""}
                onChange={function (e) {
                  up({ tafeStartDate: e.target.value });
                }}
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: 7,
                  border: `1px solid ${"#CFE0DE"}`,
                  fontSize: 14,
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
              />
            </div>
            <div>
              <label
                htmlFor={courseLengthId}
                style={{
                  display: "block",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#1A2E2B",
                  marginBottom: 5,
                }}
              >
                Length of Course <span style={{ color: "#C62828" }}>*</span>
              </label>
              <input
                id={courseLengthId}
                type="text"
                value={laptop?.tafeLength || ""}
                onChange={function (e) {
                  up({ tafeLength: e.target.value });
                }}
                placeholder="e.g. 2 years"
                style={{
                  width: "100%",
                  padding: "9px 12px",
                  borderRadius: 7,
                  border: `1px solid ${"#CFE0DE"}`,
                  fontSize: 14,
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>
        )}

        {showDeviceDelivery && (
          <div style={{ marginBottom: 14 }}>
            {generalDelivery && !laptop?.deliveryAddress && (
              <div style={{ fontSize: 12, color: "#607D79", marginBottom: 6, fontStyle: "italic" }}>
                {
                  "Pre-filled from General Info. Change below if the device should be delivered elsewhere."
                }
              </div>
            )}
            <DeliverySelect
              value={deliveryAddress}
              onChange={function (v) {
                up({ deliveryAddress: v });
              }}
              deliveryAddresses={deliveryAddresses || []}
              locked={false}
            />
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 14 }}>
          {!isTafe && (
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={hasSchoolDevice}
                onChange={function (e) {
                  up({ hasSchoolDevice: e.target.checked });
                }}
                style={{ marginTop: 2, accentColor: "#00897B" }}
              />
              <span>
                <strong>School has a device/BYOD policy</strong> — the school has an approved device
                list or BYOD program in place
              </span>
            </label>
          )}
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              fontSize: 13,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={internetSafety}
              onChange={function (e) {
                up({ internetSafety: e.target.checked });
              }}
              style={{ marginTop: 2, accentColor: "#00897B" }}
            />
            <span>
              <strong>Internet safety acknowledged</strong> — the family has been informed of
              internet safety responsibilities
            </span>
          </label>
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              fontSize: 13,
              cursor: "pointer",
              background: "#E8F5E9",
              borderRadius: 6,
              padding: "8px 10px",
              border: "1px solid #A5D6A7",
              marginLeft: -11,
              marginRight: -11,
            }}
          >
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={function (e) {
                up({ termsAccepted: e.target.checked });
              }}
              style={{ marginTop: 2, accentColor: "#00897B" }}
            />
            <span>
              <strong>I have read and accept the Terms and Conditions</strong> above on behalf of
              the parent/guardian
            </span>
          </label>
          {!isResponderJob(user) && (
            <label
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                fontSize: 13,
                cursor: "pointer",
                background: "#E8F5E9",
                borderRadius: 6,
                padding: "8px 10px",
                border: "1px solid #A5D6A7",
                marginLeft: -11,
                marginRight: -11,
                marginTop: 8,
              }}
            >
              <input
                type="checkbox"
                checked={!!laptop?.estimateAck}
                onChange={function (e) {
                  up({ estimateAck: e.target.checked });
                }}
                style={{ marginTop: 2, accentColor: "#00897B" }}
              />
              <span>
                <strong>
                  I understand that any Cost quoted for a Device is an estimate only &amp; NO
                  Authority is granted for the DFV Service to purchase.
                </strong>
              </span>
            </label>
          )}
        </div>

        <FileField
          label={`${isTafe ? "Enrolment Confirmation" : "BYOD Policy Document"} (required)`}
          file={byod}
          uploading={!!laptop?._byodUploading}
          onChange={async function (f) {
            if (!f) {
              up({ byod: null, byodConfirmed: false });
              return;
            }
            // Content-aware type gate (PDF/JPG/PNG, extension AND magic bytes must agree).
            const valid = await validateUploadFile(f);
            if (!valid.ok) {
              await alertDialog({ message: valid.error });
              return;
            }
            up({ _byodUploading: true });
            var refNo = "draft";
            var safeName = `${Date.now()}_${f.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`;
            var path = `${refNo}/byod/${safeName}`;
            const res = await uploadFile(path, f, { upsert: true, contentType: valid.contentType });
            if (res.error) {
              logger.warn("Upload failed:", res.error.message);
              up({ _byodUploading: false });
              return;
            }
            // Private bucket (db-168): store the object PATH; signed on render.
            up({ byod: path, byodConfirmed: true, _byodUploading: false });
          }}
        />

        {/* Auto-tick confirmation — sits BELOW the upload box because it ticks
            on its own once a file is attached. */}
        <label
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            fontSize: 13,
            marginTop: 10,
            cursor: "default",
          }}
        >
          <input
            type="checkbox"
            checked={!!byod}
            readOnly
            style={{ marginTop: 2, accentColor: "#00897B", cursor: "not-allowed" }}
          />
          <span style={{ color: byod ? "#388E3C" : "#607D79" }}>
            {isTafe ? (
              <span>
                <strong>Enrolment confirmation uploaded</strong> —{" "}
                {byod ? "Document attached" : "Auto-ticks once a document is uploaded above"}
              </span>
            ) : (
              <span>
                <strong>BYOD policy document uploaded</strong> —{" "}
                {byod ? "Document attached" : "Auto-ticks once a document is uploaded above"}
              </span>
            )}
          </span>
        </label>
      </div>
    </div>
  );
}

export { ChildLaptopSection };
